import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "../../style/Customerorder.css";
import { getCustomerOrder } from "../../service/Customerorder.service";
import logoDark from "../../../../assets/images/logo-dark.png"

const CustomerOrder = () => {
  // URL:
  // /26396dc3-9bcd-11f1-9d4a-50bbb51625fb
  const { orderToken } = useParams();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchOrder = async () => {
      if (!orderToken) {
        setError("Order token not found");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        console.log("ORDER TOKEN:", orderToken);
        const response = await getCustomerOrder(orderToken);
        console.log("ORDER API RESPONSE:", response);
        if (!mounted) return;

        /*
          API response expected:

          {
            success: true,
            data: {
              order: {},
              items: []
            }
          }
        */

        if (response?.success === false) {
          setError(
            response?.message || "Order details not found"
          );
          return;
        }

        // Support both:
        // response.data
        // response
        const data = response?.data ?? response;

        if (!data) {
          setError("Order details not found");
          return;
        }

        setOrderData(data);
      } catch (err) {
        console.error("ORDER API ERROR:", err);

        if (!mounted) return;

        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            "Failed to load order details"
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchOrder();

    return () => {
      mounted = false;
    };
  }, [orderToken]);

  // =========================
  // LOADING
  // =========================

  if (loading) {
    return (
      <div className="customer-order-page">
        <div className="customer-order">
          <div className="order-loading">
            Loading order details...
          </div>
        </div>
      </div>
    );
  }

  // =========================
  // ERROR
  // =========================

  if (error) {
    return (
      <div className="customer-order-page">
        <div className="customer-order">
          <div className="order-error">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="customer-order-page">
        <div className="customer-order">
          <div className="order-error">
               Order details not found
          </div>
        </div>
      </div>
    );
  }

  // =========================
  // API DATA
  // =========================

  const order = orderData?.order || {};

  const items = Array.isArray(orderData?.items)
    ? orderData.items
    : [];

  const billing = order?.billing_address || {};
  const shipping = order?.shipping_address || {};

  // =========================
  // HELPERS
  // =========================

  const formatAmount = (value) => {
    const amount = Number(value);

    if (Number.isNaN(amount)) {
      return "0.00";
    }

    return amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString("en-GB");
  };

  // =========================
  // TOTALS
  // =========================

  const totalQty = items.reduce(
    (sum, item) =>
      sum + Number(item?.quantity || 0),
    0
  );

  const totalDiscount = items.reduce(
    (sum, item) =>
      sum + Number(item?.discount_amount || 0),
    0
  );

  const totalTax = items.reduce(
    (sum, item) =>
      sum + Number(item?.taxable_amount || 0),
    0
  );

  const totalTaxable = items.reduce(
    (sum, item) =>
      sum + Number(item?.taxable_amount || 0),
    0
  );

  const totalSellingPrice = items.reduce(
    (sum, item) =>
      sum +
      Number(item?.selling_price || 0) *
        Number(item?.quantity || 0),
    0
  );

  return (
    <div className="customer-order-page">
      <div className="customer-order">

        {/* =====================================================
            HEADER
        ===================================================== */}

        <table className="order-header-table">
          <tbody>
            <tr>
              <td className="logo-column">
                <img
                  src={logoDark}
                  alt="Obsessions"
                />
              </td>

              <td className="title-column">
                <h3>Customer Order</h3>
              </td>

              <td className="cin-column">
                CIN :{" "}
                <b>
                  U74994DL2004PTC124979
                </b>
              </td>
            </tr>
          </tbody>
        </table>

        <hr className="order-hr" />

        {/* =====================================================
            SELLER / DISPATCHER / ORDER
        ===================================================== */}

        <table className="information-table">
          <tbody>
            <tr>

              {/* SELLER */}

              <td className="seller-column">
                <div className="info-content">
                  <p>
                    <b className="info-heading">
                      Seller
                    </b>

                    <br />

                    <span>
                      Envogue Furnishings India Pvt Ltd
                    </span>

                    <br />

                    Basement, 20 Pusa Road,
                    <br />

                    Karol Bagh, New Delhi, Delhi, 110005
                    <br />

                    GSTIN : 07AABCE2936K1ZL
                    <br />

                    Contact: 011-46101430 / 46184044
                    <br />

                    Toll Free: 1800 3000 3200
                    <br />

                    Email: contact@obsessions.co.in
                  </p>
                </div>
              </td>

              {/* DISPATCHER */}

              <td className="dispatcher-column">
                <div className="info-content">
                  <p>
                    <b className="info-heading">
                      Dispatcher (Shipped From)
                    </b>

                    <br />

                    <span>
                      Envogue Furnishings India Pvt Ltd
                    </span>

                    <br />

                    53/11/2, Phirni Road,
                    <br />

                    Mundka, Delhi - 110041
                    <br />

                    <b>
                      GSTIN : 07AABCE2936K1ZL
                    </b>

                    <br />

                    Mobile: 9910391099
                  </p>
                </div>
              </td>

              {/* ORDER DETAILS */}

              <td className="order-detail-column">
                <p>
                  Order ID :{" "}
                  <b>
                    {order?.order_no || "-"}
                  </b>

                  <br />

                  Order Date :{" "}
                  <span>
                    {formatDate(order?.created_at)}
                  </span>

                  <br />

                  Order Reference
                  <br />

                  Designation :
                  <br />

                  <strong>
                    Usually Ship in 2 to 3 Days
                  </strong>
                </p>
              </td>

            </tr>
          </tbody>
        </table>

        {/* =====================================================
            PURCHASER / SHIPPING
        ===================================================== */}

        <table className="information-table purchaser-table">
          <tbody>
            <tr>

              {/* PURCHASER */}

              <td className="seller-column">
                <div className="info-content">
                  <p>
                    <b className="info-heading">
                      Purchaser
                    </b>

                    <br />

                    <span>
                      {order?.customer_name || "-"}
                    </span>

                    <br />

                    {billing?.address1 || ""}
                    <br />

                    {billing?.address2 || ""}
                    <br />

                    {billing?.city || ""}
                    {billing?.state
                      ? `, ${billing.state}`
                      : ""}
                    {billing?.country
                      ? `, ${billing.country}`
                      : ""}
                    {billing?.pincode
                      ? `, ${billing.pincode}`
                      : ""}

                    <br />

                    Mobile:{" "}
                    {order?.phone || "-"}

                    <br />

                    Email:{" "}
                    {order?.email || "-"}

                    <br />

                    GSTNO:{" "}
                    {order?.gst_no || "-"}
                  </p>
                </div>
              </td>

              {/* SHIPPING */}

              <td className="dispatcher-column">
                <div className="info-content">
                  <p>
                    <b className="info-heading">
                      Shipping (Shipped To)
                    </b>

                    <br />

                    <span>
                      {order?.customer_name || "-"}
                    </span>

                    <br />

                    {shipping?.address1 || ""}
                    <br />

                    {shipping?.address2 || ""}
                    <br />

                    {shipping?.city || ""}
                    {shipping?.state
                      ? `, ${shipping.state}`
                      : ""}
                    {shipping?.country
                      ? `, ${shipping.country}`
                      : ""}
                    {shipping?.pincode
                      ? `, ${shipping.pincode}`
                      : ""}

                    <br />

                    Contact:{" "}
                    {shipping?.phone ||
                      order?.phone ||
                      "-"}

                    <br />

                    GSTNO:{" "}
                    {order?.gst_no || "-"}
                  </p>
                </div>
              </td>
              <td className="empty-column"></td>
            </tr>
          </tbody>
        </table>

        {/* =====================================================
            PRODUCT TABLE
        ===================================================== */}

        <div className="product-table-wrapper">
          <table className="product-table">
            <thead>
              <tr>
                <th className="sno">S.NO.</th>
                <th className="image">Image</th>
                <th className="description-heading">
                  PRODUCT DESCRIPTION
                </th>
                {/* <th className="barcode">Barcode</th> */}
                <th className="qty">QTY</th>
                <th className="price" style={{textAlign:"right"}}>Price</th>
                <th className="discount">Disc(%)</th>
                <th className="tax text-right" style={{textAlign:"right"}}>Tax. Amt.</th>
                <th className="total  text-right" style={{textAlign:"right"}}>Total</th>
              </tr>
            </thead>

            <tbody>

              {items.length > 0 ? (
                items.map((product, index) => (
                  <tr
                    key={
                      product?.order_detail_id ||
                      product?.id ||
                      index
                    }
                  >

                    <td className="center">
                      {index + 1}
                    </td>

                    <td className="center product-image">
                      {product?.image_path ? (
                        <img
                          src={product.image_path}
                          alt={
                            product?.quality_name ||
                            "Product"
                          }
                        />
                      ) : (
                        <span>-</span>
                      )}
                    </td>

                    <td className="product-description">
                      <b>
                        {product?.quality_name || "-"}
                      </b>

                      <br />

                      {product?.design_shade || ""}

                      {product?.size
                        ? ` - ${product.size}`
                        : ""}
                        <br/>
                         {product?.barcode || "-"}
                    </td>

                    {/* <td className="center">
                      {product?.barcode || "-"}
                    </td> */}

                    <td className="center">
                      {product?.quantity || 0}
                    </td>

                    <td className="right">
                      {formatAmount(
                        product?.selling_price
                      )}
                    </td>

                    <td className="center">
                      {product?.discount_percent || 0}
                    </td>

                    <td className="right">
                      {formatAmount(
                        product?.taxable_amount
                      )}
                    </td>

                    <td className="right">
                      {formatAmount(
                        product?.total_amount
                      )}
                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="9"
                    className="center"
                  >
                    No products found
                  </td>
                </tr>
              )}

              {/* TOTAL */}

              <tr className="total-row">

                <td
                  colSpan="3"
                  className="center total-text"
                >
                  Total
                </td>

                <td className="center total-text">
                  {totalQty}
                </td>

                <td className="right bold">
                  {formatAmount(
                    totalSellingPrice
                  )}
                </td>

                <td className="center bold">
                  {formatAmount(
                    totalDiscount
                  )}
                </td>

                <td className="right bold">
                  {formatAmount(totalTax)}
                </td>

                <td className="right bold">
                  {formatAmount(
                    order?.grand_total
                  )}
                </td>

              </tr>

              {/* TAXABLE */}

              <tr className="taxable-row">

                <td colSpan="5"></td>

                <td
                  colSpan="2"
                  className="center"
                >
                  Taxable Amt. :
                </td>

                <td className="right bold">
                  {formatAmount(
                    totalTaxable
                  )}
                </td>

              </tr>

            </tbody>
          </table>
        </div>

        {/* =====================================================
            TAX SUMMARY
        ===================================================== */}

        <table className="tax-summary">
          <thead>
            <tr>
              <th>Taxable Amt</th>
              <th>CGST Amt</th>
              <th>SGST Amt</th>
              <th>IGST Amt</th>
              <th>Total Amt.</th>
            </tr>
          </thead>

          <tbody>
            <tr>

              <td>
                {formatAmount(totalTaxable)}
              </td>

              <td>0.00</td>
              <td>0.00</td>
              <td>0.00</td>

              <td>
                {formatAmount(
                  order?.grand_total
                )}
              </td>

            </tr>
          </tbody>
        </table>

        {/* =====================================================
            NOTE
        ===================================================== */}

        <p className="order-note">
          Note: This is system generated orders details.
        </p>

      </div>
    </div>
  );
};

export default CustomerOrder;